import styles from "./creditAccountsPanel.module.css"
import { useEffect, useMemo, useState } from "react"
import { Action, Button, Column, Data, FieldType, FieldValue, InfoSection, Link, List, ModalId, SpinnerDiv, StaticValue, UIPermission, useUIActions } from "../../../../../features/ui"
import { TableName } from "../../../../../tables"
import { SaleOperation, SaleOrder, Customer } from "../../../../sales"
import { CreditAccountFilters } from "./creditAccountFilters"
import { PaymentStatus, usePriceOutlineHooks } from "../../../../operations"
import { Data2, useDataTypesHooks, Labels, useDescribeHooks } from "../../../../../features/data-types"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { useNavigate } from "react-router-dom"
import { AdjustmentNote, AdjustmentNoteType, CreditPayment } from "../../../state/types"
import { useAdjustmentNoteLoaders } from "../../../loaders/adjustmentNoteLoaders"
import { usePaymentTypeLoaders } from "../../../loaders/paymentTypeLoaders"
import { useCreditPaymentHooks } from "../../../hooks/creditPaymentHooks"
import { isDefined, round } from "../../../../../utils/typeUtils"
import { useSystemStateHooks } from "../../../../system"
import { DownloadCreditAccountStatusPDFModal } from "../credit-account-status-pdf/downloadCreditAccountStatusPDFModal"

type Props = {
    sales: SaleOrder[]
    operations: SaleOperation[]
    customers: Customer[]
    creditPayments: CreditPayment[]
}

export const CreditAccountsPanel = (props: Props) => {
    const { sales, operations, customers, creditPayments } = props

    const [customerId, setCustomerId] = useState<string>()
    const [creditLimit, setCreditLimit] = useState<number>()
    const [debt, setDebt] = useState<number>()
    const [grossDebt, setGrossDebt] = useState<number>()
    const [rows, setRows] = useState<Data2[]>([])
    const [adjustmentNotes, setAdjustmentNotes] = useState<AdjustmentNote[]>([])
    const [asyncDataLoaded, setAsyncDataLoaded] = useState(false)
    const [currentFilters, setCurrentFilters] = useState<{from?: Date, to?: Date}>({})

    const { openModal } = useUIActions()

    const { describe } = useDescribeHooks()
    const { getCustomerDebtData, getOperationDueDate } = useCreditPaymentHooks()
    const { userHasPermission } = useSystemStateHooks()
    const canCreateCreditPayments = userHasPermission(UIPermission.CREATE_CREDIT_PAYMENTS)
    const { formatPrice } = useDataTypesHooks()
    const { getRoutePath } = useRoutesHooks()
    const { getPriceOutline } = usePriceOutlineHooks()
    const navigate = useNavigate()

    const adjustmentNoteLoaders = useAdjustmentNoteLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()

    const findCustomer = (id?: string) => customers.find(customer => customer.id === id)

    useEffect(() => {
        const customer = findCustomer(customerId)
        setCreditLimit(customer?.creditAccount?.creditLimit)
        setDebt(undefined)
        setGrossDebt(undefined)
        if (!customerId) {
            return
        }
        getCustomerDebtData(customerId).then(debtData => {
            const creditInFavor = customer?.creditInFavor || 0
            setGrossDebt(round(debtData.debt))
            setDebt(round(debtData.debt - creditInFavor))
        })
    }, [customerId, customers])

    const readOnly = useMemo(() =>
        !canCreateCreditPayments || !customerId || !isDefined(creditLimit),
        [canCreateCreditPayments, customerId, creditLimit]
    );

    const isSale = (operationId: string) => !!sales.find(sale => sale.id === operationId)

    const setRowsState = async (
        operationsList: SaleOperation[],
        creditPaymentsList: CreditPayment[]
    ) => {
        // Adjustment notes are shown as their own rows: the operation keeps its original total as debit
        const { loadOperationsAdjustmentNotes } = adjustmentNoteLoaders()
        const loadedAdjustmentNotes = await loadOperationsAdjustmentNotes(operationsList.map(operation => operation.id))
        setAdjustmentNotes(loadedAdjustmentNotes)
        const isDebitNote = (adjustmentNote: AdjustmentNote) => adjustmentNote.type === AdjustmentNoteType.DEBIT
        // A debit note (e.g. a hoard extension) is already included in the operation current total
        const getDebitNotesAmount = (operationId: string) => round(loadedAdjustmentNotes
            .filter(adjustmentNote => adjustmentNote.operationId === operationId && isDebitNote(adjustmentNote))
            .reduce((sum, adjustmentNote) => sum + adjustmentNote.amount, 0))

        const operationsData: Data2[] = await Promise.all(
            operationsList.map(async operation => {
                const isSaleOperation = isSale(operation.id)
                const table = isSaleOperation ? TableName.SALE_ORDERS : TableName.HOARD_ORDERS
                const operationTotal = round(getPriceOutline(table, operation).total - getDebitNotesAmount(operation.id))
                const dueDate = await getOperationDueDate(operation)

                return {
                    id: operation.id,
                    date: operation.creationDate,
                    operation: operation.id,
                    operationLabel: describe(table, operation),
                    operationIcon: isSaleOperation ? 'tags' : 'layers',
                    paymentStatus: operation.paymentStatus,
                    debit: formatPrice(operationTotal),
                    debitValue: operationTotal,
                    dueDate
                }
            })
        )

        const adjustmentNotesData: Data2[] = loadedAdjustmentNotes.map(adjustmentNote => {
            const isDebit = isDebitNote(adjustmentNote)
            return {
                id: adjustmentNote.id,
                date: adjustmentNote.date,
                operation: adjustmentNote.id,
                operationLabel: describe(TableName.ADJUSTMENT_NOTES, adjustmentNote),
                operationIcon: 'file-earmark-text',
                ...(isDebit ?
                    { debit: formatPrice(adjustmentNote.amount), debitValue: adjustmentNote.amount } :
                    { credit: formatPrice(adjustmentNote.amount) })
            }
        })

        // Payments collected directly on a credit account operation (e.g. a hoard extension collected at the moment).
        // The credit in favor used isn't listed: it was already listed when it was paid on account
        const directPaymentsData: Data2[] = operationsList.flatMap(operation => operation.paymentItems
            .filter(paymentItem => !paymentItem.creditInFavor)
            .map((paymentItem, index) => {
                const table = isSale(operation.id) ? TableName.SALE_ORDERS : TableName.HOARD_ORDERS
                const paymentName = paymentItem.isRounding ? 'Redondeo' : paymentTypeLoaders().findPaymentType(paymentItem.paymentTypeId)?.name
                return {
                    id: `${operation.id}-payment-${index}`,
                    date: paymentItem.paymentDate,
                    operation: `${operation.id}-payment-${index}`,
                    operationId: operation.id,
                    operationLabel: `Cobro ${describe(table, operation)}${paymentName ? ` (${paymentName})` : ''}`,
                    operationIcon: 'cash-stack',
                    credit: formatPrice(paymentItem.amount),
                    creditValue: paymentItem.amount
                }
            })
        )
        const creditPaymentsData: Data2[] = creditPaymentsList.map((creditPayment) => {
            const operationItemsTotal = creditPayment.operationItems?.reduce((acc, item) => acc + item.amount, 0) ?? 0
            const creditAmount = Math.max(creditPayment.amount, operationItemsTotal)
            return {
                id: creditPayment.id,
                date: creditPayment.creationDate,
                operation: creditPayment.id,
                operationLabel: describe(TableName.CREDIT_PAYMENTS, creditPayment),
                operationIcon: 'cash-stack',
                credit: formatPrice(creditAmount),
                creditValue: creditAmount
            }
        })

        setRows(operationsData.concat(adjustmentNotesData, directPaymentsData, creditPaymentsData)
            .sort((rowA, rowB) => (rowB.date! as string).localeCompare(rowA.date! as string))
        )
        setAsyncDataLoaded(true)
    }

    const getDataTypePath = (dataTypeId: string) => {
        // An adjustment note or direct payment row goes to its operation
        const operationId = adjustmentNotes.find(adjustmentNote => adjustmentNote.id === dataTypeId)?.operationId ||
            rows.find(row => row.id === dataTypeId)?.operationId as string | undefined ||
            dataTypeId
        const isOperation = !!operations.find(operation => operation.id === operationId)
        return isOperation ?
            getRoutePath(isSale(operationId) ? RoutePath.SALES : RoutePath.HOARDS, operationId) :
            getRoutePath(RoutePath.CREDIT_PAYMENTS, dataTypeId)
    }

    const columns: Column[] = [
        {
            name: 'date',
            type: FieldType.DATE,
            label: 'Fecha'
        },
        {
            name: 'operation',
            type: FieldType.TEXT,
            label: 'Operación',
            render: (value?: FieldValue) => {
                const operationId = value! as string
                const { operationLabel, operationIcon } = rows.find(row => row.id === operationId)!
                return (
                    <Link
                        text={operationLabel! as string}
                        icon={operationIcon! as string}
                        kind="primary"
                        to={getDataTypePath(operationId)}
                    />
                )
            }
        },
        {
            name: "paymentStatus",
            type: FieldType.TEXT,
            label: "Estado de Pago",
            render: (value?: FieldValue) => value ? Labels.paymentStatusLabel(value as PaymentStatus) : '-'
        },
        {
            name: 'debit',
            type: FieldType.TEXT,
            label: 'Debe'
        },
        {
            name: 'credit',
            type: FieldType.TEXT,
            label: 'Haber'
        },
        {
            name: 'dueDate',
            type: FieldType.DATE,
            label: 'Fecha Vto.',
            render: (value?: FieldValue) => <StaticValue value={value} type={FieldType.DATE} />
        }
    ]

    const actions = (id: string): Action[] => {
        return [
            { icon: 'eye-fill', kind: 'tertiary', tooltip: 'Ver Detalle', href: getDataTypePath(id) }
        ]
    }

    const onSearchStart = () => setAsyncDataLoaded(false)

    const onSearchDone = (operationsList: SaleOperation[], creditPaymentsList: CreditPayment[]) => setRowsState(operationsList, creditPaymentsList)

    const onCreate = () => {
        const options = { state: { customerId }}
        navigate(`${customerId}/${RoutePath.CREATE}`, options)
    }

    const currentLimit = creditLimit ?? 0;
    const currentDebt = debt ?? 0;
    const isPaymentOnAccount = currentDebt <= 0;
    // currentDebt ya descuenta el crédito a favor; si es negativo, ese saldo se muestra aparte y el disponible no supera el crédito otorgado.
    const availableCredit = round(currentLimit - Math.max(currentDebt, 0));

    // El panel y el estado de cuenta (PDF/XLSX) muestran el saldo total de la cuenta, no el de las filas filtradas por fecha.
    // Si hay crédito a favor se muestra aparte. Solo cuando hay deuda y crédito a la vez se muestra la diferencia,
    // sin signo y con su sentido: "Saldo a Pagar" (la deuda es mayor) o "Saldo a Favor" (el crédito es mayor).
    const customerCreditInFavor = findCustomer(customerId)?.creditInFavor || 0;
    const hasCreditInFavor = customerCreditInFavor > 0;
    const totalDebt = grossDebt ?? 0;
    const creditBalance = hasCreditInFavor ? customerCreditInFavor : undefined;
    const netBalance = hasCreditInFavor && totalDebt > 0 && currentDebt !== 0 ? Math.abs(currentDebt) : undefined;
    const netBalanceLabel = currentDebt > 0 ? 'Saldo a Pagar' : 'Saldo a Favor';

    const infoContent = useMemo(() => (
        <div className={styles.infoContainer}>
            <div className={styles.infoField}>
                <span>Crédito Otorgado</span>
                <span>{formatPrice(currentLimit)}</span>
            </div>

            <div className={styles.infoField}>
                <span>Crédito Disponible</span>
                <span>{formatPrice(availableCredit)}</span>
            </div>

            <div className={styles.infoField}>
                <span>Deuda Total</span>
                <span>{formatPrice(totalDebt)}</span>
            </div>

            {creditBalance !== undefined && (
                <div className={styles.infoField}>
                    <span>Crédito a Favor</span>
                    <span>{formatPrice(creditBalance)}</span>
                </div>
            )}

            {netBalance !== undefined && (
                <div className={styles.infoField}>
                    <span>{netBalanceLabel}</span>
                    <span>{formatPrice(netBalance)}</span>
                </div>
            )}
        </div>
    ), [creditLimit, debt, grossDebt, customerCreditInFavor]);

    const renderAsync = (element: JSX.Element) => asyncDataLoaded ? element : <SpinnerDiv />

    const emptyMessage = customerId
        ? "No hay operaciones en las fechas seleccionadas."
        : "Seleccionar un cliente."

    return (
        <>
            <CreditAccountFilters
                operations={operations}
                creditPayments={creditPayments}
                onChangeCustomer={setCustomerId}
                onSearchStart={onSearchStart}
                onSearchDone={onSearchDone}
                onDateChange={(from, to) => setCurrentFilters({ from, to })}
            />

            {customerId && (
                <div className={styles.downloadWrapper}>
                    <Button
                        label="Descargar Estado de Cuenta" 
                        icon="file-earmark-pdf"
                        kind="tertiary"
                        onClick={() => openModal(ModalId.DOWNLOAD_CREDIT_ACCOUNT_STATUS_PDF)}
                    />
                </div>
            )}

            {customerId && isDefined(creditLimit) && (
                <InfoSection content={infoContent} loaded={true} />
            )}
            {renderAsync(
                <List
                    columns={columns}
                    rows={customerId ? rows as Data[] : []}
                    rowId="id"
                    createLabel={isPaymentOnAccount ? "Pago a Cuenta" : "Nuevo Cobro"}
                    emptyMessage={emptyMessage}
                    readOnly={readOnly}
                    actions={actions}
                    onCreate={onCreate}
                />
            )}
            
            {customerId && (
                <DownloadCreditAccountStatusPDFModal
                    customerId={customerId}
                    customer={findCustomer(customerId)}
                    customerName={findCustomer(customerId)?.name || ''}
                    rows={rows}
                    summary={{
                        limit: formatPrice(currentLimit),
                        available: formatPrice(availableCredit),
                        debt: formatPrice(totalDebt),
                        creditBalance: creditBalance !== undefined ? formatPrice(creditBalance) : undefined,
                        netBalance: netBalance !== undefined ? formatPrice(netBalance) : undefined,
                        netBalanceLabel
                    }}
                    summaryValues={{
                        limit: currentLimit,
                        available: availableCredit,
                        debt: totalDebt,
                        creditBalance,
                        netBalance,
                        netBalanceLabel
                    }}
                    dateFrom={currentFilters.from}
                    dateTo={currentFilters.to}
                />
            )}
        </>
    )
}
